'use client';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import { useUserProvider } from '../providers/user/UserProvider';

function AccountsUpdateModal() {
  const [open, setOpen] = useState<boolean>(false);
  const { showAccountsUpdateModal } = useUserProvider();

  useEffect(() => {
    if (showAccountsUpdateModal) {
      setOpen(true);
    }
  }, [showAccountsUpdateModal]);

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        '&.MuiBox-root': {
          border: 'none',
        },
        '&:focus': {
          outline: 'none',
        },
      }}
    >
      <Box
        sx={{
          '&:focus': {
            outline: 'none',
          },
        }}
      >
        <AccountsUpdateContent handleClose={() => setOpen(false)} />
      </Box>
    </Modal>
  );
}

export default AccountsUpdateModal;

const AccountsUpdateContent = (props: { handleClose: () => void }) => {
  return (
    <div className="m-4 flex min-w-[300px] max-w-[500px] flex-col rounded-3xl bg-base-200 p-6 md:min-w-[350px]">
      <div className="mb-5 flex w-full flex-row">
        <div className="grow text-center">
          <span className="text-lg font-bold leading-tight">Announcing a Unified Account System Across All Teuteuf Games</span>
        </div>
        <Link href={'#'} onClick={props.handleClose}>
          <CloseRoundedIcon className="m-0 fill-current text-2xl leading-none" />
        </Link>
      </div>
      <div className="mb-5 flex flex-col gap-2 text-sm text-gray-900 dark:text-white">
          <p>We've updated our accounts system! You can now:</p>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <AccountIcon />
              <p>Manage your account for all Teuteuf games in one place.</p>
            </div>
            <div className="flex items-center gap-2">
              <GamesIcon />
              <p>
                Access all our games from the{" "}
                <a
                  href="https://account.teuteuf.fr/#games"
                  target="blank"
                  rel="noreferrer"
                  className="text-blue-500 underline hover:text-blue-600"
                >
                  My Games
                </a>{" "}
                page.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SubscriptionIcon />
              <p>
                Select from a new set of plans for a premium, ad-free
                experience.
              </p>
            </div>
          </div>
        </div>
      <a
        href="https://account.teuteuf.fr/"
        target='_blank'
        rel="noreferrer"
        className="btn btn-primary mb-2 h-[33px] min-h-[33px] shrink px-5 text-sm font-normal text-white md:h-[40px] md:min-h-[40px] md:px-8 md:text-base"
      >
        Login now
      </a>
      <button
        type="button"
        onClick={props.handleClose}
        className="btn h-[33px] min-h-[33px] shrink border border-[#645C70] bg-white px-5 text-sm font-normal text-[#230351] hover:bg-[#F6F0FE] md:h-[40px] md:min-h-[40px] md:px-8 md:text-base"
      >
        Got it, thanks!
      </button>
    </div>
  );
};

const AccountIcon = () => {
  return (
    <div className="w-6 min-w-6">
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21M8 7C8 8.06087 8.42143 9.07828 9.17157 9.82843C9.92172 10.5786 10.9391 11 12 11C13.0609 11 14.0783 10.5786 14.8284 9.82843C15.5786 9.07828 16 8.06087 16 7C16 5.93913 15.5786 4.92172 14.8284 4.17157C14.0783 3.42143 13.0609 3 12 3C10.9391 3 9.92172 3.42143 9.17157 4.17157C8.42143 4.92172 8 5.93913 8 7Z"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  );
};

const GamesIcon = () => {
  return (
    <div className="w-6 min-w-6">
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6 12H10M8 10V14M15 11V11.01M18 13V13.01M2 8C2 7.46957 2.21071 6.96086 2.58579 6.58579C2.96086 6.21071 3.46957 6 4 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V8Z"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  );
};

const SubscriptionIcon = () => {
  return (
    <div className="w-6 min-w-6">
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12.5 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V7C4 6.46957 4.21071 5.96086 4.58579 5.58579C4.96086 5.21071 5.46957 5 6 5H18C18.5304 5 19.0391 5.21071 19.4142 5.58579C19.7893 5.96086 20 6.46957 20 7V10M16 3V7M8 3V7M4 11H16M20 14L22 16M22 16H19C18.5137 16 18.0347 16.1182 17.6042 16.3444C17.1738 16.5707 16.8047 16.8982 16.529 17.2987C16.2532 17.6992 16.0789 18.1608 16.0212 18.6437C15.9634 19.1265 16.0239 19.6162 16.1974 20.0705C16.3709 20.5248 16.6523 20.9301 17.0173 21.2514C17.3822 21.5728 17.8198 21.8007 18.2924 21.9154C18.765 22.0301 19.2584 22.0281 19.73 21.9098C20.2017 21.7914 20.6375 21.5602 21 21.236M22 16L20 18"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  );
};